import React from 'react';
import PropTypes from 'prop-types';
import {
  TablePagination,
  TableRow,
  Switch,
  FormControlLabel,
  IconButton,
  Hidden,
} from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import { withStyles } from '@material-ui/core/styles';
import { green } from '@material-ui/core/colors';
import UploadIcon from '@material-ui/icons/CloudUpload';
import { inject, observer, PropTypes as mobxPropTypes } from 'mobx-react';
import { ROLE_LABEL, ROLE_ADMIN } from '../../constants/roles';
import DataTable from '../../components/DataTable/DataTable';
import TableCell from '../../components/DataTable/TableCell';
import UserSearchForm from '../../components/UserSearchForm/UserSearchForm';


const styles = theme => ({
  colorSwitchBase: {
    color: theme.palette.error.dark,
    '&$colorSwitchChecked': {
      color: green[600],
      '& + $colorSwitchBar': {
        backgroundColor: green[600],
      },
    },
  },
  colorSwitchBar: {
    color: theme.palette.error.dark,
    backgroundColor: theme.palette.error.dark,
  },
  colorSwitchChecked: {},
  switchLabelDisabled: {
    color: theme.palette.error.dark,
  },
  switchLabelEnabled: {
    color: green[600],
  },
});

const propTypes = {
  userStore: mobxPropTypes.observableObject.isRequired,
  snackbarStore: mobxPropTypes.observableObject.isRequired,
  classes: PropTypes.oneOfType([PropTypes.object]).isRequired,
  history: PropTypes.oneOfType([PropTypes.object]).isRequired,
  paginate: PropTypes.bool,
  showEmptyRows: PropTypes.bool,
  search: PropTypes.bool,
  defaultLimit: PropTypes.number,
};

const defaultProps = {
  paginate: false,
  search: false,
  showEmptyRows: false,
  defaultLimit: 50,
};

class UsersTable extends React.Component {
  constructor(props) {
    super(props);
    const { defaultLimit } = this.props;
    this.state = {
      data: [],
      limit: defaultLimit,
      filters: { order_by: '-created_at', is_active: true },
      total: 0,
      page: 0,
    };
    this.handleChangePage = this.handleChangePage.bind(this);
    this.handleChangeRowsPerPage = this.handleChangeRowsPerPage.bind(this);
    this.fetchData = this.fetchData.bind(this);
    this.renderPagination = this.renderPagination.bind(this);
    this.renderRows = this.renderRows.bind(this);
    this.handleEditClick = this.handleEditClick.bind(this);
    this.handleUploadClick = this.handleUploadClick.bind(this);
    this.handleStatusChange = this.handleStatusChange.bind(this);
  }

  componentWillMount() {
    this.fetchData();
  }

  static getTableHeader() {
    return [
      { id: 1, label: 'Name', hidden: { xsDown: true } },
      { id: 2, label: 'Email' },
      { id: 3, label: 'Role', hidden: { mdDown: true } },
      { id: 4, label: 'Status', hidden: { xsDown: true } },
      { id: 5, label: '' },
    ];
  }

  async fetchData(searchFilters = null) {
    const { userStore, loadingStore } = this.props;
    loadingStore.setReady(false);
    const { page, limit, filters } = this.state;
    const offset = limit * page;
    const newFilters = searchFilters || filters;
    const params = Object.assign({ offset, limit }, newFilters);
    try {
      const users = await userStore.api.users.get({ id: null, params });
      this.setState({
        data: users.objects,
        filters: newFilters,
        total: users.meta.total_count,
      });
    } catch (error) {
      this.setState({
        data: [],
        filters: newFilters,
      });
    }
    loadingStore.setReady(true);
  }

  handleChangePage(event, page) {
    this.setState({ page }, () => this.fetchData());
  }

  handleChangeRowsPerPage(event) {
    this.setState({ limit: event.target.value }, () => this.fetchData());
  }

  handleEditClick(row) {
    const { history } = this.props;
    history.push(`/users/edit/${row.id}`);
  }

  handleUploadClick(row) {
    const { history } = this.props;
    history.push(`/reports/add/${row.id}`);
  }

  async handleStatusChange(row) {
    const { userStore, snackbarStore, loadingStore } = this.props;
    const { data } = this.state;
    const newStatus = !row.is_active;
    loadingStore.setReady(false);
    try {
      await userStore.api.users.updateStatus(row.id, newStatus);
      const idx = data.findIndex(u => (u.id === row.id));
      data[idx].is_active = newStatus;
      this.setState({
        data,
      });
    } catch (error) {
      snackbarStore.setError({
        title: 'There was an error updating the status. Please, try again later.',
      });
    }
    loadingStore.setReady(true);
  }

  renderRows() {
    const { data } = this.state;
    const { classes } = this.props;
    return (
      data.map(row => (
        <TableRow
          hover
          tabIndex={-1}
          key={row.id}
        >
          <Hidden xsDown>
            <TableCell component="th" scope="row" padding="default">
              {row.full_name}
            </TableCell>
          </Hidden>
          <TableCell>
            {row.email}
          </TableCell>
          <Hidden mdDown>
            <TableCell>
              {ROLE_LABEL[row.role]}
            </TableCell>
          </Hidden>
          <Hidden xsDown>
            <TableCell>
              <FormControlLabel
                control={(
                  <Switch
                    checked={Boolean(row.is_active)}
                    onChange={() => this.handleStatusChange(row)}
                    value={`switch-status-${row.id}`}
                    classes={{
                      switchBase: classes.colorSwitchBase,
                      checked: classes.colorSwitchChecked,
                      bar: classes.colorSwitchBar,
                    }}
                  />
                )}
                classes={{
                  label: row.is_active ? classes.switchLabelEnabled : classes.switchLabelDisabled,
                }}
                label={row.is_active ? 'Enabled' : 'Disabled'}
              />
            </TableCell>
          </Hidden>
          <TableCell padding="none" isActions>
            {row.role !== ROLE_ADMIN && (
            <IconButton aria-label="Edit" color="primary" onClick={() => this.handleUploadClick(row)}>
              <UploadIcon />
            </IconButton>)}
            <IconButton aria-label="Edit" color="primary" onClick={() => this.handleEditClick(row)}>
              <EditIcon />
            </IconButton>
          </TableCell>
        </TableRow>
      ))
    );
  }

  renderPagination() {
    const { total, limit, page } = this.state;
    const { defaultLimit } = this.props;
    let rowsPerPageOptions = [5, 10, 25];
    if (defaultLimit > 10) {
      rowsPerPageOptions = [25, 50, 75];
    }
    return (
      <TablePagination
        component="div"
        count={total}
        rowsPerPage={limit}
        rowsPerPageOptions={rowsPerPageOptions}
        page={page}
        backIconButtonProps={{
          'aria-label': 'Previous Page',
        }}
        nextIconButtonProps={{
          'aria-label': 'Next Page',
        }}
        onChangePage={this.handleChangePage}
        onChangeRowsPerPage={this.handleChangeRowsPerPage}
      />
    );
  }

  renderSearch() {
    return (<UserSearchForm onSubmit={this.fetchData} />);
  }

  render() {
    const { data, limit, page } = this.state;
    const { paginate, search, showEmptyRows } = this.props;
    let emptyRows = 0;
    if (showEmptyRows) {
      emptyRows = limit - Math.min(limit, data.length - page * limit);
    }
    const searchComponent = (search) ? this.renderSearch() : null;
    return (
      <div>
        {searchComponent}
        <DataTable
          header={UsersTable.getTableHeader()}
          renderRows={this.renderRows}
          emptyRows={emptyRows}
          paginate={paginate}
          renderPagination={this.renderPagination}
        />
      </div>
    );
  }
}

UsersTable.propTypes = propTypes;
UsersTable.defaultProps = defaultProps;
const UsersTableInjected = inject('userStore', 'snackbarStore', 'loadingStore')(observer(UsersTable));
export default withStyles(styles)(UsersTableInjected);



// WEBPACK FOOTER //
// ./src/containers/UsersTable/UsersTable.js